// frontend/src/components/cohesion/OfflineBanner.tsx
// Brighella v2 (ADR-014 D11) — offline indicator banner.
//
// Listens to window 'online' / 'offline' events. When offline, renders a
// banner at the top of the page explaining that the app is in read-only
// mode and writes will be blocked. Disappears as soon as connectivity
// returns.

import { useEffect, useState } from 'react'
import type { CSSProperties } from 'react'
import { useLanguage } from '../../i18n/index.jsx'

export default function OfflineBanner() {
  const { t } = useLanguage()
  // Initialise from current navigator state so the banner is correct on
  // first paint even if the page loads offline.
  const [online, setOnline] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine,
  )

  useEffect(() => {
    const goOnline = () => setOnline(true)
    const goOffline = () => setOnline(false)
    window.addEventListener('online', goOnline)
    window.addEventListener('offline', goOffline)
    return () => {
      window.removeEventListener('online', goOnline)
      window.removeEventListener('offline', goOffline)
    }
  }, [])

  if (online) return null

  const bannerStyle: CSSProperties = {
    position: 'sticky',
    top: 0,
    zIndex: 9000,
    padding: '6px 12px',
    textAlign: 'center',
    fontSize: 'var(--text-sm)',
    fontWeight: 600,
    background: 'var(--warning-dim, #fef3c7)',
    color: 'var(--warning, #92400e)',
    borderBottom: '1px solid var(--warning, #f59e0b)',
  }

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="offline-banner"
      style={bannerStyle}
    >
      {t('offlineBanner')}
    </div>
  )
}
